import React, { useState } from "react";
import { linkIdentifier } from "../../../../utils/linkIdentifier";
import IconRoundButton from "../../CommonButtons/IconRoundButton";
import PrimaryButton from "../../CommonButtons/PrimaryButton";
import SVGComponent from "../../SVGComponent";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";
import ICONS from "../../../../assets/Icons";

// Suggestions of props for component while using the components -------------------------------------
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("button" | "submit")} BtnType
 */
/**
 * @typedef {("white" | "black")} HoverOption
 */
/**
 * @param {Object} props
 * @param {string} props.headerTitle - Main title text to display at the top of popup
 * @param {string} [props.headerFontWeight] - Font weight for header title eg:- "normal", "semibold", "bold"
 * @param {string} [props.headerFontColor] - Text color class for header eg:- "text-primary", "text-secondary", "failed"
 * @param {string} [props.textSize] - Font size class for header eg:- "font-sm", "font-md", "font-lg"
 * @param {string} [props.headerWithIcon] - Icon/SVG image path to display next to header title
 * @param {string} [props.secondHeaderTitle] - Optional secondary header text below main title
 * @param {string} [props.secondHeaderFontWeight] - Font weight for secondary header eg:- "normal", "semibold"
 * @param {string} [props.secondHeaderFontColor] - Text color class for secondary header eg:- "text-primary"
 * @param {string} [props.secondHeaderTextSize] - Font size class for secondary header eg:- "font-sm"
 * @param {string} [props.subtext] - Subtitle/description text below headers
 * @param {string} [props.closeenabledBgColor] - Background color when close button is enabled (CSS variable)
 * @param {boolean} [props.closeisLoading] - Show loading state on close button (true/false)
 * @param {OnClickEventHandler} [props.closehandleFunction] - Handle function when close button is clicked
 * @param {string} [props.closeiconImg] - Icon/SVG image path for close button
 * @param {HoverOption} [props.closehoverOption] - Hover effect option for close button eg:- "white" or "black"
 * @param {BtnType} [props.closebtnType] - Button type for close button eg:- "button" or "submit"
 * @param {boolean} [props.closeisDisabled] - Disable close button (true/false)
 * @param {string} [props.closedisabledBgColor] - Background color when close button is disabled (CSS variable)
 * @param {React.RefObject} [props.closebuttonRef] - Button ref for close button (optional)
 * @param {string} [props.closeimgColor] - Icon color for close button (CSS variable)
 * @param {string} [props.alertSubtext] - Alert subtext/description text
 * @param {string} [props.alertHeaderOne] - Primary alert header text
 * @param {string} [props.alertHeaderTwo] - Secondary alert header text
 * @param {boolean} [props.isneedalertSecondHeader] - Whether to show the secondary alert header
 * @param {string} [props.alertHeadertextcolor] - Text color class for alert headers
 * @param {string} [props.alertHeadertweight] - Font weight for alert headers eg:- "normal", "semibold", "bold"
 * @param {string} [props.alertHeaderTextSize] - Font size class for alert headers
 * @param {string} [props.alertSubtextTextSize] - Font size class for alert subtext
 * @param {string} [props.alertSubtexttextcolor] - Text color class for alert subtext
 * @param {string} [props.alertSubtextHeader] - Optional header text displayed above the alert subtext
 * @param {string} [props.alertSubTextHeaderColor] - Text color class for the alert subtext header
 * @param {boolean} [props.isneedsecondHeaderTitle] - Show/hide second header title (true/false)
 * @param {string} props.btnText - Text to display on the primary action button
 * @param {OnClickEventHandler} props.btnhandleFunction - Handle function when the primary action button is clicked
 * @param {boolean} [props.isDisabled] - Disable the primary action button (true/false)
 * @param {string} [props.enabledTextColor] - Text color when the primary action button is enabled (CSS variable)
 * @param {string} [props.disabledTextColor] - Text color when the primary action button is disabled (CSS variable)
 * @param {string} [props.enabledBgColor] - Background color when the primary action button is enabled (CSS variable)
 * @param {string} [props.disabledBgColor] - Background color when the primary action button is disabled (CSS variable)
 * @param {boolean} [props.isLoading] - Show loading state on the primary action button (true/false)
 * @param {HoverOption} [props.btnhoverOption] - Hover effect option for the primary action button eg:- "white" or "black"
 * @param {React.RefObject} [props.buttonRef] - Button ref for the primary action button (optional)
 * @param {BtnType} [props.btnType] - Button type for the primary action button eg:- "button" or "submit"
 * @param {string} [props.btnOutLineColor] - Border/outline color for the primary action button (CSS variable)
 * @param {boolean} [props.btnFullWidthRequired] - Make the primary action button full width (true/false)
 * @param {boolean} [props.btnInActiveVariantNeeded] - Use inactive variant for the primary action button (true/false)
 * @param {boolean} [props.btnIsLoaderNeeded] - Show loader on the primary action button (true/false)
 * @returns {JSX.Element}
 */
const SmallAlertPopup = ({
  headerTitle,
  subtext,
  closehandleFunction,
  closeisDisabled,
  closedisabledBgColor,
  closebuttonRef,
  closeimgColor,
  alertSubtext,
  alertHeaderOne,
  alertHeaderTwo,
  btnText,
  btnhandleFunction,
  isDisabled,
  enabledTextColor,
  enabledBgColor,
  disabledTextColor,
  disabledBgColor,
  isLoading,
  btnhoverOption,
  buttonRef,
  btnOutLineColor,
  btnFullWidthRequired,
  btnInActiveVariantNeeded,
  btnIsLoaderNeeded,
  isneedalertSecondHeader,
  headericonimgColor,
  isDataArray,
}) => {
  const [animate, setAnimate] = useState(false);

  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  const needsFullStop = (text = "") => !text.trim().endsWith(".");

  return (
    <div
      className="flex w-full h-screen justify-center items-center m-auto top-top-half left-left-half fixed z-z-index-modal p-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`max-h-alert-popup-height-1 xxl:max-h-alert-popup-height-2 tall:max-h-alert-popup-height-2 overflow-hidden w-full  Qmd:w-small-popup-width  flex flex-col   bg-primary-bg rounded-border-radius-md animate-none ${isDataArray?.length > GlobalConfiguration.lengthCheckZero ? "" : "h-alert-no-data-found-height"} ${animate ? "Qmd:animate-slideInFromLeft" : "Qmd:animate-slideInFromRight"}`}
      >
        <div className="h-full flex flex-col flex-1 overflow-hidden">
          <div className=" flex flex-col gap-gap-xs px-padding-md pt-padding-md">
            <div className="flex justify-between gap-gap-md ">
              <div className=" flex gap-gap-sm">
                {" "}
                <SVGComponent
                  color={headericonimgColor}
                  height="var(--icon-md)"
                  width="var(--icon-md)"
                  url={ICONS?.ALERT}
                  alt="CustomIcon"
                />
                <p className={`text-text-primary  text-font-md  font-semibold`}>
                  {toSentenceCase(headerTitle)}
                </p>
              </div>
              <IconRoundButton
                enabledBgColor={"var(--primary-nav-act)"}
                isLoading={false}
                handleFunction={() => {
                  if (!closeisDisabled) {
                    setAnimate(true);
                    setTimeout(() => {
                      closehandleFunction();
                    }, IntervalConfiguration.intervalSemiMid);
                  }
                }}
                title="Close"
                icon={ICONS?.CLOSE}
                hoverOption={"white"}
                btnType={"button"}
                isDisabled={closeisDisabled}
                disabledBgColor={closedisabledBgColor}
                buttonRef={closebuttonRef}
                imgColor={closeimgColor}
              />
            </div>
            <div>
              {subtext && (
                <p className="text-font-xs text-text-secondary font-normal ">
                  {linkIdentifier(subtext)}
                  {needsFullStop(subtext) && <span>.</span>}
                </p>
              )}
            </div>
          </div>

          <div className="w-full h-full mt-margin-md  px-padding-md flex flex-col gap-gap-md mb-margin-md flex-1 overflow-y-scroll">
            {isDataArray.length > GlobalConfiguration.lengthCheckZero ? (
              isDataArray?.map((item, index) => {
                return (
                  <div className="rounded-border-radius-sm  bg-primary-card ">
                    <div className="px-padding-md py-padding-sm flex flex-col ">
                      <div>
                        <p className="text-text-primary font-semibold text-font-sm">
                          {item.headerName}
                          {item?.count && (
                            <>
                              {"  -  "}
                              <span>{item.count}</span>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex flex-col gap-gap-xs pt-padding-xs">
                        <p
                          className={` text-font-xs font-normal  text-text-secondary`}
                        >
                          {linkIdentifier(item.subtext)}
                        </p>
                      </div>
                      <div className="flex justify-end  pt-padding-sm">
                        <div>
                          <PrimaryButton
                            btnText={item.btnText}
                            btnType={"button"}
                            handleFunction={() => {
                              if (!item.isDisabled) {
                                setAnimate(true);
                                setTimeout(() => {
                                  item?.handleFunction?.();
                                }, IntervalConfiguration.intervalSemiMid);
                              }
                            }}
                            isDisabled={item.isDisabled}
                            enabledTextColor={enabledTextColor}
                            disabledTextColor={disabledTextColor}
                            enabledBgColor={enabledBgColor}
                            disabledBgColor={disabledBgColor}
                            outLineColor={btnOutLineColor}
                            fullWidthRequired={btnFullWidthRequired}
                            hoverOption={btnhoverOption}
                            isLoading={isLoading}
                            buttonRef={buttonRef}
                            inActiveVariantNeeded={btnInActiveVariantNeeded}
                            isLoaderNeeded={btnIsLoaderNeeded}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div
                className={`flex flex-col items-center  mt-margin-lg gap-3 justify-center m-auto  max-w-reminder-popup-no-data-width `}
              >
                <img
                  src={ICONS?.NO_DATA_FOUND}
                  alt="No data found"
                  className="w-graphic-width-md h-graphic-rectangle-height-md mb-margin-sm"
                />

                <p className="text-text-primary text-font-xs text-center ">
                  There are no new alerts.
                </p>
                {/* no data found view all */}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmallAlertPopup;
